/**
 * Platform-neutral id generation. Uses the Web Crypto standard `crypto`, which
 * is present on browsers, Node 20+, and React Native (with a polyfill) — so the
 * core stays neutral (decision #10) while producing real UUIDv4s.
 */
import type {
  AssetId,
  CharacterId,
  ChatId,
  GameCardId,
  GeneratedImageId,
  InstalledRqtPackId,
  LorebookEntryId,
  LorebookId,
  MessageId,
  ParticipantId,
  PersonaId,
  PresetId,
  ProviderProfileId,
  ScriptId,
} from "../domain/common.js";

function getCrypto(): Crypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || typeof c.getRandomValues !== "function") {
    throw new Error(
      "Web Crypto is unavailable in this environment. Provide a crypto polyfill.",
    );
  }
  return c;
}

/** Generate a RFC4122 v4 UUID string. */
export function uuid(): string {
  const c = getCrypto();
  if (typeof c.randomUUID === "function") return c.randomUUID();
  // Fallback for environments without randomUUID but with getRandomValues.
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

// Branded-id constructors. At runtime these are plain UUID strings; the brand
// only exists at the type level to prevent id-kind mix-ups.
export const newCharacterId = () => uuid() as CharacterId;
export const newPersonaId = () => uuid() as PersonaId;
export const newChatId = () => uuid() as ChatId;
export const newMessageId = () => uuid() as MessageId;
export const newLorebookId = () => uuid() as LorebookId;
export const newLorebookEntryId = () => uuid() as LorebookEntryId;
export const newPresetId = () => uuid() as PresetId;
export const newAssetId = () => uuid() as AssetId;
export const newGeneratedImageId = () => uuid() as GeneratedImageId;
export const newProviderProfileId = () => uuid() as ProviderProfileId;
export const newScriptId = () => uuid() as ScriptId;
export const newParticipantId = () => uuid() as ParticipantId;
export const newGameCardId = () => uuid() as GameCardId;
export const newInstalledRqtPackId = () => uuid() as InstalledRqtPackId;
